import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingComponent } from '@app/admin/manage/modals/loading-component/loading.component';
import { PageComponent } from '@app/v2/components/page/page.component';
import { AccessRequestService } from '@app/v2/services/access-request/access-request.service';
import { AlertComponent } from '@components/atoms/alert/alert.component';
import {
    MdsButtonComponent,
    MdsCardComponent,
    MdsCardContentComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { EnvConfigurationService } from '@services/env/env-configuration.service';
import { map, of, shareReplay, switchMap, tap } from 'rxjs';

@Component({
    selector: 'medpace-access-request-page',
    templateUrl: './access-request.page.component.html',
    imports: [
        PageComponent,
        AlertComponent,
        MdsCardComponent,
        MdsCardContentComponent,
        LoadingComponent,
        MdsButtonComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessRequestPageComponent {
    private readonly accessRequestService = inject(AccessRequestService);
    private readonly activeRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly envConfigurationService = inject(EnvConfigurationService);
    applicationName = this.envConfigurationService.configuration.applicationName;
    readonly isLoading = signal(true);
    errorMessage: WritableSignal<string | null> = signal(null);

    readonly guid$ = this.activeRoute.queryParamMap.pipe(
        map((params) => params.get('guid')),
        shareReplay(1)
    );
    readonly guid = toSignal(this.guid$);

    readonly invitation$ = this.guid$.pipe(
        switchMap((guid) => {
            if (!guid) {
                this.errorMessage.set('The invitation id is not correct.');
                return of(null);
            }
            return this.accessRequestService.getInvitation(guid).pipe(
                tap((invitation) => {
                    if (!invitation) {
                        this.errorMessage.set('There is no invitation in the system.');
                    }
                })
            );
        }),
        tap(() => this.isLoading.set(false))
    );

    readonly invitation = toSignal(this.invitation$);

    async signIn() {
        this.isLoading.set(true);
        const navigated = await this.router.navigate(['/access-request/logged'], {
            queryParams: { guid: this.guid() },
        });
        if (navigated) {
            this.isLoading.set(false);
        }
    }

    register() {
        this.router.navigate(['/access-request'], { queryParams: { guid: this.guid() } });
    }
}
